/**
 * UiManager: DOM UI layer (crosshair, hint, info panel, status, error screen,
 * scene fade, loader with progress bar, scene tag, top menu + help screen).
 *
 * Subscribes to the EventBus and renders scene/hotspot/zone/portal events as
 * DOM. UI is kept entirely outside PlayCanvas scripts, per the architecture doc.
 */

import { eventBus } from '../events/EventBus';
import { globalConfig } from '../config/global.config';
import type { HotspotConfig, TriggerZoneConfig } from '../config/manifest';

/** Loader background frame-animation config (replaces the old clouds <video>).
 *  Frames live in public/assets/images/baiyun/frame_NNNN.jpg. */
const LOADER_FRAMES_DIR = '/assets/images/baiyun';
const LOADER_FRAMES_COUNT = 61;
const LOADER_FRAMES_FPS = 12;

/** Friendly per-scene loader text, keyed by scene name (manifest.name, which
 *  SCENE_LOAD_START carries). Falls back to the generic message below. */
const LOADER_TEXT_BY_SCENE: Record<string, string> = {
    laomendong2: '正在加载金陵古街',
    meihuashan: '正在加载梅花仙境'
};
const LOADER_TEXT_DEFAULT = '正在加载场景…';

class UiManager {
    private _hint: HTMLElement;
    private _panel: HTMLElement;
    private _panelTitle: HTMLElement;
    private _panelContent: HTMLElement;
    private _status: HTMLElement;
    private _errorScreen: HTMLElement;
    private _errorMessage: HTMLElement;
    private _fade: HTMLElement;
    private _loader: HTMLElement;
    private _loaderBg: HTMLImageElement;
    /** Loader background frame-animation state (replaces the old <video>). */
    private _loaderFrameRaf = 0;
    private _loaderFrameStart = 0;
    private _loaderFrameIndex = -1;
    private _loaderFrameImages: HTMLImageElement[] = [];
    private _loaderFramesPreloaded = false;
    /** 帧图是否已开始批量下载。延后到首个进度 tick（或 8s 兜底）：~5.3MB
     *  的 JPEG 不能在 0% 阶段与场景 manifest / .sog 头抢带宽。 */
    private _loaderFramesKicked = false;
    private _loaderFrameKickTimer: ReturnType<typeof setTimeout> | null = null;
    private _loaderText: HTMLElement;
    private _progressBar: HTMLElement;
    private _progressPct: HTMLElement;
    private _sceneTag: HTMLElement;
    private _helpScreen!: HTMLElement;
    private _consoleScreen!: HTMLElement;
    private _consoleBody!: HTMLElement;
    private _consoleLines: string[] = [];
    private _mediaScreen: HTMLElement | null = null;
    private _confirmScreen: HTMLElement | null = null;
    /** Called when a popup (panel/media/confirm) opens, so the host can release
     *  the pointer lock and the user can click the close button with the mouse. */
    requestUnlock?: () => void;
    /** Called when any popup (panel/media) opens — host can pause the scene video. */
    onPopupOpen?: () => void;
    /** Called when any popup (panel/media) closes — host can restore the scene video. */
    onPopupClose?: () => void;
    private _hotspots: Map<string, HotspotConfig>;
    private _zones: Map<string, TriggerZoneConfig>;
    private _activeHotspots = new Set<string>();
    /** rAF handle for the progress easing animation. */
    private _progressRaf = 0;
    private _progressTarget = 0;
    private _loaderActive = false;

    constructor(hotspots: HotspotConfig[] = [], zones: TriggerZoneConfig[] = []) {
        this._hint = document.getElementById('hint')!;
        this._panel = document.getElementById('info-panel')!;
        this._panelTitle = document.getElementById('panel-title')!;
        this._panelContent = document.getElementById('panel-content')!;
        this._status = document.getElementById('status')!;
        this._errorScreen = document.getElementById('error-screen')!;
        this._errorMessage = document.getElementById('error-message')!;
        this._fade = document.getElementById('fade')!;
        this._loader = document.getElementById('loader')!;
        this._loaderBg = document.getElementById('loader-bg') as HTMLImageElement;
        this._loaderText = document.getElementById('loader-text')!;
        this._sceneTag = document.getElementById('scene-tag')!;

        // Progress bar inside the loader (built in JS so index.html stays simple).
        this._progressBar = document.createElement('div');
        this._progressBar.id = 'progress-bar';
        this._progressPct = document.createElement('div');
        this._progressPct.id = 'progress-pct';
        const track = document.createElement('div');
        track.id = 'progress-track';
        track.appendChild(this._progressBar);
        this._loader.appendChild(track);
        this._loader.appendChild(this._progressPct);

        this._hotspots = new Map(hotspots.map((h) => [h.id, h]));
        this._zones = new Map(zones.map((z) => [z.id, z]));

        document.getElementById('panel-close')!.addEventListener('click', () => this.hidePanel());

        this._buildTopMenu();
        this._buildConsoleScreen();
        this._installConsoleCapture();
        this._subscribe();
    }

    /** Rebind hotspot/zone lookup tables when a new scene loads.
     *  Arrays are optional: a scene using only `objects[]` has no legacy arrays. */
    bindScene(hotspots?: HotspotConfig[], zones?: TriggerZoneConfig[]) {
        this._hotspots = new Map((hotspots ?? []).map((h) => [h.id, h]));
        this._zones = new Map((zones ?? []).map((z) => [z.id, z]));
        this._activeHotspots.clear();
        this.hidePanel();
    }

    private _subscribe() {
        eventBus.on('HOTSPOT_ENTER', (e) => {
            if (e.id) this._activeHotspots.add(e.id);
            this._refreshHint();
        });
        eventBus.on('HOTSPOT_EXIT', (e) => {
            if (e.id) this._activeHotspots.delete(e.id);
            this._refreshHint();
        });
        eventBus.on('HOTSPOT_CLICK', (e) => {
            const h = e.id ? this._hotspots.get(e.id) : undefined;
            if (h) this.showPanel(h.panel.title, h.panel.content);
        });
        eventBus.on('ZONE_ENTER', (e) => {
            const z = e.id ? this._zones.get(e.id) : undefined;
            if (z?.panel) this.showPanel(z.panel.title, z.panel.content);
        });
        eventBus.on('LOAD_ERROR', (e) => this.showError(e.message ?? '加载失败'));
        eventBus.on('MODE_CHANGE', (e) => {
            const movement = e.moveMode === 'walk' ? '漫游 (Walk)' : '飞行 (Fly)';
            this.setStatus(e.viewMode === 'third' ? `模式：第三人称 · ${movement}` : `模式：${movement}`);
        });
        eventBus.on('SCENE_LOAD_START', (e) => {
            if ((e.progress ?? 0) >= 1) return;
            const text = (e.name && LOADER_TEXT_BY_SCENE[e.name]) || LOADER_TEXT_DEFAULT;
            if (!this._loaderActive || (e.progress ?? 0) <= 0) this.showLoader(text);
            else this._loaderText.textContent = text;
            if (typeof e.progress === 'number') this.updateProgress(e.progress);
        });
        eventBus.on('SCENE_READY', (e) => {
            this.updateProgress(1);
            // Let the bar visually reach 100% before hiding.
            window.setTimeout(() => this.hideLoader(), 250);
            this._sceneTag.textContent = '';
            this._sceneTag.classList.add('hidden');
        });
        eventBus.on('PORTAL_ENTER', (e) => {
            this.showHint(`传送至「${e.targetScene ?? ''}」…`);
        });
        eventBus.on('OBJECT_TRIGGER', (e) => {
            const actions = e.actions ?? (e.action ? [e.action] : []);
            for (const a of actions) this._dispatchAction(a);
        });
    }

    /** Dispatch a single event action to its UI effect. switchScene/custom are
     *  handled by main.ts (also subscribed to OBJECT_TRIGGER). */
    private async _dispatchAction(a: import('../scene/SceneObjectTypes').SceneEventAction) {
        const p = a.payload;
        switch (a.type) {
            case 'showPanel':
                this.showPanel(p.title ?? a.name, p.content ?? '');
                break;
            case 'openUrl':
                if (p.url) window.open(p.url, '_blank', 'noopener');
                break;
            case 'playVideo':
                this.showMedia('video', p.videoUrl ?? p.url ?? '', p.title ?? a.name);
                break;
            case 'showImage':
                this.showMedia('image', p.imageUrl ?? p.url ?? '', p.title ?? a.name);
                break;
            case 'callApi':
                try {
                    const res = await fetch(p.apiUrl ?? '', { method: p.method ?? 'GET' });
                    const text = await res.text();
                    if (p.onSuccess === 'showPanel') this.showPanel(p.title ?? a.name, text);
                    else if (p.onSuccess === 'openUrl' && p.url) window.open(p.url, '_blank', 'noopener');
                } catch (err) {
                    this.showPanel('接口调用失败', String(err));
                }
                break;
            // switchScene / custom: handled by main.ts.
        }
    }

    /** Show a video/image media overlay with a custom video player. */
    showMedia(kind: 'video' | 'image', url: string, title: string) {
        if (!this._mediaScreen) this._buildMediaScreen();
        this.requestUnlock?.();
        const el = this._mediaScreen!;
        const body = el.querySelector('.media-body') as HTMLElement;
        this._clearMediaBody(body);
        const ttl = el.querySelector('.media-title') as HTMLElement;
        ttl.textContent = title;
        if (kind === 'video') {
            // Build custom video player (no native controls)
            const wrapper = document.createElement('div');
            wrapper.className = 'vp-wrapper';
            const v = document.createElement('video');
            v.src = url;
            v.autoplay = true;
            v.playsInline = true;
            v.setAttribute('playsinline', '');
            v.setAttribute('webkit-playsinline', '');
            v.setAttribute('disablepictureinpicture', '');
            wrapper.appendChild(v);

            // Control bar
            const controls = document.createElement('div');
            controls.className = 'vp-controls';
            const playBtn = document.createElement('button');
            playBtn.className = 'vp-btn vp-play';
            playBtn.textContent = '⏸';
            const seek = document.createElement('input');
            seek.type = 'range';
            seek.className = 'vp-seek';
            seek.min = '0';
            seek.max = '1000';
            seek.value = '0';
            const timeLabel = document.createElement('span');
            timeLabel.className = 'vp-time';
            timeLabel.textContent = '0:00 / 0:00';
            const fsBtn = document.createElement('button');
            fsBtn.className = 'vp-btn vp-fs';
            fsBtn.textContent = '⛶';

            controls.append(playBtn, seek, timeLabel, fsBtn);
            wrapper.appendChild(controls);
            body.appendChild(wrapper);

            // --- Video event handlers ---
            const fmtTime = (t: number) => {
                if (!isFinite(t)) return '0:00';
                const m = Math.floor(t / 60);
                const s = Math.floor(t % 60);
                return `${m}:${s.toString().padStart(2, '0')}`;
            };
            let seekDragging = false;
            const onTimeUpdate = () => {
                const pct = v.duration ? (v.currentTime / v.duration) * 1000 : 0;
                if (!seekDragging) seek.value = String(pct);
                timeLabel.textContent = `${fmtTime(v.currentTime)} / ${fmtTime(v.duration)}`;
            };
            const onPlay = () => { playBtn.textContent = '⏸'; };
            const onPause = () => { playBtn.textContent = '▶'; };

            v.addEventListener('loadedmetadata', () => { onTimeUpdate(); });
            v.addEventListener('timeupdate', onTimeUpdate);
            v.addEventListener('play', onPlay);
            v.addEventListener('pause', onPause);
            v.addEventListener('ended', () => { playBtn.textContent = '▶'; });

            // Play/pause toggle
            playBtn.addEventListener('click', (e) => { e.stopPropagation(); v.paused ? v.play() : v.pause(); });
            // Click video to toggle play/pause
            v.addEventListener('click', () => { v.paused ? v.play() : v.pause(); });

            // Seek bar
            seek.addEventListener('input', () => {
                seekDragging = true;
                if (v.duration) v.currentTime = (Number(seek.value) / 1000) * v.duration;
            });
            seek.addEventListener('change', () => { seekDragging = false; });

            // CSS fullscreen toggle (stays inside #app-root, inherits virtual-landscape)
            let cssFs = false;
            const toggleFs = () => {
                cssFs = !cssFs;
                el.classList.toggle('css-fullscreen', cssFs);
                fsBtn.textContent = cssFs ? '⤧' : '⛶';
            };
            fsBtn.addEventListener('click', (e) => { e.stopPropagation(); toggleFs(); });

            // Prevent controls clicks from bubbling to body (which closes overlay)
            controls.addEventListener('click', (e) => e.stopPropagation());

            // Auto-hide controls after 5s of mouse inactivity
            let hideTimer: ReturnType<typeof setTimeout> | null = null;
            const showControls = () => {
                wrapper.classList.remove('vp-idle');
                if (hideTimer) clearTimeout(hideTimer);
                hideTimer = setTimeout(() => { wrapper.classList.add('vp-idle'); }, 5000);
            };
            wrapper.addEventListener('mousemove', showControls);
            wrapper.addEventListener('touchstart', showControls, { passive: true });
            // Start the timer immediately
            showControls();
        } else {
            const img = document.createElement('img');
            img.src = url; img.style.maxWidth = '80vw'; img.style.maxHeight = '70vh';
            body.appendChild(img);
        }
        el.classList.remove('hidden');
        this.onPopupOpen?.();
    }

    private _buildMediaScreen() {
        const el = document.createElement('div');
        el.id = 'media-screen';
        el.className = 'hidden';
        el.innerHTML = `
            <div class="media-box">
                <div class="media-header"><span class="media-title"></span><button class="media-close">×</button></div>
                <div class="media-body"></div>
            </div>`;
        const root = document.getElementById('app-root') ?? document.body;
        root.appendChild(el);
        el.querySelector('.media-close')!.addEventListener('click', () => this._hideMediaScreen());
        el.addEventListener('click', (e) => { if (e.target === el) this._hideMediaScreen(); });
        this._mediaScreen = el;
    }

    private _hideMediaScreen(): void {
        if (!this._mediaScreen) return;
        const wasOpen = !this._mediaScreen.classList.contains('hidden');
        const body = this._mediaScreen.querySelector('.media-body') as HTMLElement | null;
        if (body) this._clearMediaBody(body);
        this._mediaScreen.classList.remove('css-fullscreen');
        this._mediaScreen.classList.add('hidden');
        if (wasOpen) this.onPopupClose?.();
    }

    private _clearMediaBody(body: HTMLElement): void {
        for (const video of body.querySelectorAll('video')) {
            video.pause();
            video.removeAttribute('src');
            video.load();
        }
        body.innerHTML = '';
    }

    /** Modal confirm dialog. Resolves true on 确认, false on 取消/dismiss. Used by
     *  requireConfirm portals (and any editor flow that needs an async yes/no). */
    showConfirm(text: string, confirmText = '确认', cancelText = '取消'): Promise<boolean> {
        if (!this._confirmScreen) this._buildConfirmScreen();
        const el = this._confirmScreen!;
        const body = el.querySelector('.confirm-text') as HTMLElement;
        body.textContent = text;
        const ok = el.querySelector('.confirm-ok') as HTMLButtonElement;
        const cancel = el.querySelector('.confirm-cancel') as HTMLButtonElement;
        ok.textContent = confirmText;
        cancel.textContent = cancelText;
        el.classList.remove('hidden');
        this.requestUnlock?.();
        return new Promise<boolean>((resolve) => {
            const done = (val: boolean) => {
                ok.onclick = null;
                cancel.onclick = null;
                el.classList.add('hidden');
                resolve(val);
            };
            ok.onclick = () => done(true);
            cancel.onclick = () => done(false);
        });
    }

    private _buildConfirmScreen() {
        const el = document.createElement('div');
        el.id = 'confirm-screen';
        el.className = 'hidden';
        el.innerHTML = `
            <div class="confirm-box">
                <div class="confirm-text"></div>
                <div class="confirm-actions">
                    <button class="confirm-cancel"></button>
                    <button class="confirm-ok"></button>
                </div>
            </div>`;
        const root2 = document.getElementById('app-root') ?? document.body;
        root2.appendChild(el);
        this._confirmScreen = el;
    }

    private _refreshHint() {
        if (this._activeHotspots.size === 0) {
            this.hideHint();
            return;
        }
        const ids = Array.from(this._activeHotspots);
        const last = ids[ids.length - 1];
        const h = this._hotspots.get(last);
        this.showHint(h ? `${h.name} — 点击或按 E 查看详情` : '点击查看详情');
    }

    // ── Top menu + help ───────────────────────────────────────────────────
    private _buildTopMenu() {
        const menu = document.createElement('div');
        menu.id = 'top-menu';

        const helpBtn = document.createElement('button');
        helpBtn.id = 'help-btn';
        helpBtn.textContent = '帮助';
        helpBtn.addEventListener('click', () => this.toggleHelp());
        helpBtn.style.display = 'none';
        menu.appendChild(helpBtn);

        const consoleBtn = document.createElement('button');
        consoleBtn.id = 'console-btn';
        consoleBtn.textContent = 'Console';
        consoleBtn.addEventListener('click', () => this.toggleConsole());
        consoleBtn.style.display = 'none';
        menu.appendChild(consoleBtn);

        const uiLayer = document.getElementById('ui-layer') ?? document.body;
        uiLayer.appendChild(menu);

        this._helpScreen = document.createElement('div');
        this._helpScreen.id = 'help-screen';
        this._helpScreen.className = 'hidden';
        this._helpScreen.innerHTML = `
            <div class="help-box">
                <div class="help-header">
                    <span class="help-title">操作帮助</span>
                    <button id="help-close" class="help-close" aria-label="关闭">×</button>
                </div>
                <div class="help-body">
                    <section class="help-card">
                        <h3>移动与视角</h3>
                        <div class="help-rows">
                            <div class="help-row"><kbd>W</kbd><kbd>A</kbd><kbd>S</kbd><kbd>D</kbd><span>前后左右移动</span></div>
                            <div class="help-row"><kbd>鼠标</kbd><span>按住左键拖动旋转视角</span></div>
                            <div class="help-row"><kbd>Shift</kbd><span>加速跑</span></div>
                            <div class="help-row"><kbd>Ctrl</kbd><span>减速</span></div>
                            <div class="help-row"><kbd>Space</kbd><span>跳跃（行走模式）</span></div>
                            <div class="help-row"><kbd>E</kbd>/<kbd>Q</kbd><span>升降（飞行模式）</span></div>
                            <div class="help-row"><kbd>V</kbd><span>切换 行走 / 飞行</span></div>
                            <div class="help-row"><kbd>滚轮</kbd><span>第三人称相机距离</span></div>
                        </div>
                    </section>

                    <section class="help-card">
                        <h3>右下角控制按钮</h3>
                        <div class="help-rows">
                            <div class="help-row"><b>一 / 三</b><span>第一人称 / 第三人称视角切换</span></div>
                            <div class="help-row"><b>飞 / 游</b><span>行走 / 飞行模式切换</span></div>
                            <div class="help-row"><b>走 / 跑</b><span>移动速度切换</span></div>
                            <div class="help-row"><b>跳</b><span>跳跃</span></div>
                            <div class="help-row"><b>人物动作</b><span>展开动作列表：再见 · 举手 · 挥手 · 鼓掌 · 欢呼 · 跳舞 · 跪下</span></div>
                            <div class="help-row"><b>视频控制</b><span>场景含视频屏时出现，可播放 / 暂停</span></div>
                        </div>
                    </section>

                    <section class="help-card">
                        <h3>场景交互</h3>
                        <div class="help-rows">
                            <div class="help-row"><kbd>点击</kbd><span>打开热点信息面板</span></div>
                            <div class="help-row"><span class="help-mouse">走入传送门</span><span>切换场景（需在多人房间中）</span></div>
                            <div class="help-row"><kbd>R</kbd><span>回到出生点（需在多人房间中）</span></div>
                            <div class="help-row"><kbd>Esc</kbd><span>关闭面板 / 弹窗</span></div>
                            <div class="help-row"><kbd>F8</kbd><span>GSplat 调试（LOD 着色 / 八叉树）</span></div>
                        </div>
                    </section>

                    <section class="help-card">
                        <h3>房间与多人</h3>
                        <div class="help-rows">
                            <div class="help-row"><b>⚙ 设置</b><span>左上角打开菜单：世界场景 · 创建场景 · 加入场景 · 角色 · 分享链接 · 公告 · 帮助 · 反馈</span></div>
                            <div class="help-row"><b>创建场景</b><span>选择场景模板、填写房间名与最大人数，一键开房</span></div>
                            <div class="help-row"><b>加入场景</b><span>进入他人创建的房间，密码房间需输入密码</span></div>
                            <div class="help-row"><b>角色</b><span>预览并更换形象，保存后重新进入场景生效</span></div>
                            <div class="help-row"><b>分享链接</b><span>复制邀请链接，好友打开即可加入同一房间</span></div>
                        </div>
                    </section>

                    <section class="help-card">
                        <h3>触屏操作</h3>
                        <div class="help-rows">
                            <div class="help-row"><span class="help-mouse">左侧摇杆</span><span>控制移动</span></div>
                            <div class="help-row"><span class="help-mouse">右侧区域</span><span>拖动旋转视角</span></div>
                            <div class="help-row"><span class="help-mouse">横屏</span><span>建议横屏并全屏体验</span></div>
                        </div>
                    </section>
                </div>
            </div>`;
        uiLayer.appendChild(this._helpScreen);
        this._helpScreen.querySelector('#help-close')!.addEventListener('click', () => this.toggleHelp(false));
        this._helpScreen.addEventListener('click', (e) => {
            if (e.target === this._helpScreen) this.toggleHelp(false);
        });
    }

    private _buildConsoleScreen(): void {
        const uiLayer = document.getElementById('ui-layer') ?? document.body;
        this._consoleScreen = document.createElement('div');
        this._consoleScreen.id = 'console-screen';
        this._consoleScreen.className = 'hidden';
        this._consoleScreen.innerHTML = `
            <div class="console-box">
                <div class="console-header">
                    <span class="console-title">Console</span>
                    <div class="console-actions">
                        <button class="console-copy">Copy</button>
                        <button class="console-clear">Clear</button>
                        <button class="console-close">Close</button>
                    </div>
                </div>
                <pre id="console-body"></pre>
            </div>`;
        uiLayer.appendChild(this._consoleScreen);
        this._consoleBody = this._consoleScreen.querySelector('#console-body') as HTMLElement;
        this._consoleScreen.querySelector('.console-close')!.addEventListener('click', () => this.toggleConsole(false));
        this._consoleScreen.querySelector('.console-copy')!.addEventListener('click', () => void this._copyConsoleLogText());
        this._consoleScreen.querySelector('.console-clear')!.addEventListener('click', () => {
            this._consoleLines = [];
            this._renderConsole();
        });
        this._appendConsoleLine('info', ['Console capture ready']);
    }

    private _installConsoleCapture(): void {
        const original = {
            log: console.log.bind(console),
            info: console.info.bind(console),
            warn: console.warn.bind(console),
            error: console.error.bind(console)
        };
        console.log = (...args: unknown[]) => {
            original.log(...args);
            this._appendConsoleLine('log', args);
        };
        console.info = (...args: unknown[]) => {
            original.info(...args);
            this._appendConsoleLine('info', args);
        };
        console.warn = (...args: unknown[]) => {
            original.warn(...args);
            this._appendConsoleLine('warn', args);
        };
        console.error = (...args: unknown[]) => {
            original.error(...args);
            this._appendConsoleLine('error', args);
        };
        window.addEventListener('error', (event) => {
            this._appendConsoleLine('error', [event.message, event.filename, event.lineno, event.colno]);
        });
        window.addEventListener('unhandledrejection', (event) => {
            this._appendConsoleLine('error', ['unhandledrejection', event.reason]);
        });
    }

    toggleConsole(show?: boolean): void {
        const open = show ?? this._consoleScreen.classList.contains('hidden');
        this._consoleScreen.classList.toggle('hidden', !open);
        if (open) this.requestUnlock?.();
    }

    private _appendConsoleLine(level: string, args: unknown[]): void {
        const time = new Date().toLocaleTimeString();
        const text = args.map((arg) => this._formatConsoleArg(arg)).join(' ');
        this._consoleLines.push(`[${time}] ${level.toUpperCase()} ${text}`);
        if (this._consoleLines.length > 300) this._consoleLines.splice(0, this._consoleLines.length - 300);
        this._renderConsole();
    }

    private _formatConsoleArg(arg: unknown): string {
        if (arg instanceof Error) return `${arg.name}: ${arg.message}\n${arg.stack ?? ''}`.trim();
        if (typeof arg === 'string') return arg;
        try {
            return JSON.stringify(arg);
        } catch {
            return String(arg);
        }
    }

    private async _copyConsoleLogText(): Promise<void> {
        const text = this._consoleLines.join('\n');
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
            } else if (!this._copyTextWithTextarea(text)) {
                throw new Error('clipboard unavailable');
            }
            this._appendConsoleLine('info', ['Console log copied']);
        } catch (err) {
            this._appendConsoleLine('warn', ['Console log copy failed', err]);
        }
    }

    private _copyTextWithTextarea(text: string): boolean {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        textarea.style.top = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        try {
            return document.execCommand('copy');
        } finally {
            textarea.remove();
        }
    }

    private _renderConsole(): void {
        this._consoleBody.textContent = this._consoleLines.join('\n');
        this._consoleBody.scrollTop = this._consoleBody.scrollHeight;
    }

    toggleHelp(show?: boolean) {
        const open = show ?? this._helpScreen.classList.contains('hidden');
        this._helpScreen.classList.toggle('hidden', !open);
    }

    // ── Fade ───────────────────────────────────────────────────────────────
    fadeOut(): Promise<void> { return this._setFade(true); }
    fadeIn(): Promise<void> { return this._setFade(false); }
    private _setFade(show: boolean): Promise<void> {
        return new Promise((resolve) => {
            const done = () => {
                this._fade.removeEventListener('transitionend', done);
                resolve();
            };
            this._fade.addEventListener('transitionend', done);
            requestAnimationFrame(() => this._fade.classList.toggle('show', show));
            window.setTimeout(done, globalConfig.fadeDuration + 120);
        });
    }

    // ── Loader + progress ──────────────────────────────────────────────────
    showLoader(text: string) {
        this._loaderText.textContent = text;
        this._loader.classList.remove('hidden');
        this._loaderBg.classList.remove('hidden');
        this._startLoaderFrameAnim();
        this._loaderActive = true;
        this._progressTarget = 0;
        this._setProgress(0);
        this._stopProgressAnim();
    }

    /** Set the bar to a known fraction (0..1) from real asset progress, taking
     *  the max so a later smaller reported value never moves the bar backward. */
    updateProgress(frac: number) {
        const f = Math.max(0, Math.min(1, frac));
        if (f >= 1) {
            this._progressTarget = 1;
            this._stopProgressAnim();
            this._setProgress(1);
            return;
        }
        this._progressTarget = Math.max(this._progressTarget, f);
        this._ensureProgressAnim();
        // 首个真实进度 tick ⇒ 场景 chunk 已在流动，此时才允许加载视频帧开下
        // （见 _startLoaderFrameAnim 的说明，避免拖长 0% 阶段）。
        if (this._loaderActive && f > 0) this._kickLoaderFrames();
    }

    hideLoader() {
        this._stopProgressAnim();
        this._setProgress(1);
        this._loaderActive = false;
        this._loader.classList.add('hidden');
        this._stopLoaderFrameAnim();
        this._loaderBg.classList.add('hidden');
    }

    private _setProgress(frac: number) {
        const pct = Math.round(frac * 100);
        this._progressBar.style.width = `${pct}%`;
        this._progressPct.textContent = `${pct}%`;
    }

    /** Ease the displayed value toward the latest real load target. */
    private _ensureProgressAnim() {
        if (this._progressRaf) return;
        const tick = () => {
            const cur = parseFloat(this._progressBar.style.width) / 100 || 0;
            const next = cur + (this._progressTarget - cur) * 0.08;
            this._setProgress(next);
            if (this._progressTarget < 1 && next < this._progressTarget - 0.001) {
                this._progressRaf = requestAnimationFrame(tick);
            } else {
                this._progressRaf = 0;
            }
        };
        this._progressRaf = requestAnimationFrame(tick);
    }

    private _stopProgressAnim() {
        if (this._progressRaf) cancelAnimationFrame(this._progressRaf);
        this._progressRaf = 0;
    }

    // ── Loader background frame animation ─────────────────────────────────
    private _ensureLoaderFramesPreloaded() {
        if (this._loaderFramesPreloaded) return;
        this._loaderFramesPreloaded = true;
        for (let i = 0; i < LOADER_FRAMES_COUNT; i++) {
            const img = new Image();
            img.decoding = 'async';
            img.src = `${LOADER_FRAMES_DIR}/frame_${String(i + 1).padStart(4, '0')}.jpg`;
            this._loaderFrameImages.push(img);
        }
    }

    private _startLoaderFrameAnim() {
        this._loaderFrameIndex = -1;
        // 静态海报立即上 —— 与 index.html <img> 同 URL（已缓存，零请求）。
        // 其余 60 帧（约 5.3MB）此时不下：0% 阶段场景 manifest 和 .sog 头
        // 还需要带宽，61 张并行 JPEG 会正好拖长这段等待。首个进度 tick
        // 或 8s 兜底后再开始批量下载。
        this._loaderBg.src = `${LOADER_FRAMES_DIR}/frame_0001.jpg`;
        if (this._loaderFrameKickTimer) clearTimeout(this._loaderFrameKickTimer);
        this._loaderFrameKickTimer = setTimeout(() => this._kickLoaderFrames(), 8000);
        // Begin the loop only after every frame has finished decoding, so the
        // animation never stalls mid-loop waiting on a frame that isn't ready
        // (the old per-frame `complete` gate caused visible freezes). 批量下载
        // kick 之前图片列表为空，loop 只是保持海报不动，帧到位后自然接上。
        this._ensureLoaderFramesReady().then(() => {
            if (!this._loaderActive) return; // loader was hidden while waiting
            this._loaderFrameStart = performance.now();
            this._loaderFrameIndex = -1;
            const tick = () => {
                if (!this._loaderActive) return;
                const elapsed = performance.now() - this._loaderFrameStart;
                const idx = Math.floor((elapsed / 1000) * LOADER_FRAMES_FPS) % LOADER_FRAMES_COUNT;
                if (idx !== this._loaderFrameIndex) {
                    this._loaderFrameIndex = idx;
                    const img = this._loaderFrameImages[idx];
                    if (img) this._loaderBg.src = img.src;
                }
                this._loaderFrameRaf = requestAnimationFrame(tick);
            };
            this._loaderFrameRaf = requestAnimationFrame(tick);
        });
    }

    /** 开始延后的加载帧批量下载。 */
    private _kickLoaderFrames() {
        if (this._loaderFrameKickTimer) {
            clearTimeout(this._loaderFrameKickTimer);
            this._loaderFrameKickTimer = null;
        }
        if (this._loaderFramesKicked) return;
        this._loaderFramesKicked = true;
        this._ensureLoaderFramesPreloaded();
    }

    /** Resolve once every loader frame has decoded (or errored, so a single
     *  failed frame never blocks the loop from starting). */
    private _ensureLoaderFramesReady(): Promise<void> {
        return Promise.all(this._loaderFrameImages.map((img) => {
            if (img.complete && img.naturalWidth > 0) return Promise.resolve();
            return new Promise<void>((resolve) => {
                img.addEventListener('load', () => resolve(), { once: true });
                img.addEventListener('error', () => resolve(), { once: true });
            });
        })).then(() => undefined);
    }

    private _stopLoaderFrameAnim() {
        if (this._loaderFrameKickTimer) {
            clearTimeout(this._loaderFrameKickTimer);
            this._loaderFrameKickTimer = null;
        }
        if (this._loaderFrameRaf) {
            cancelAnimationFrame(this._loaderFrameRaf);
            this._loaderFrameRaf = 0;
        }
    }

    // ── Hint / panel / status / error ──────────────────────────────────────
    private _hintTimer: ReturnType<typeof setTimeout> | null = null;

    showHint(text: string, durationMs = 3000) {
        this._hint.textContent = text;
        this._hint.classList.remove('hidden');
        if (this._hintTimer) clearTimeout(this._hintTimer);
        this._hintTimer = setTimeout(() => this.hideHint(), durationMs);
    }
    hideHint() {
        if (this._hintTimer) { clearTimeout(this._hintTimer); this._hintTimer = null; }
        this._hint.classList.add('hidden');
    }
    showPanel(title: string, content: string) {
        this._panelTitle.textContent = title;
        this._panelContent.textContent = content;
        this._panel.classList.remove('hidden');
        this.requestUnlock?.();
        this.onPopupOpen?.();
    }
    hidePanel() {
        const wasOpen = !this._panel.classList.contains('hidden');
        this._panel.classList.add('hidden');
        if (wasOpen) this.onPopupClose?.();
    }
    setStatus(text: string) {
        this._status.textContent = text;
        this._status.classList.remove('hidden');
        window.setTimeout(() => this._status.classList.add('hidden'), 2500);
    }
    showError(message: string) {
        this._errorMessage.textContent = message;
        this._errorScreen.classList.remove('hidden');
    }
    hideError() { this._errorScreen.classList.add('hidden'); }
}

export { UiManager };
